/**
 *
 *
 * @author Jaap
 * @date 23-09-2019
 */
import ContactPanel from "./ContactPanel";
import RegisterPanel from "./RegisterPanel";
import SearchPanel from "./SearchPanel";
import SharePanel from "./SharePanel";
import SpreadsPanel from "./SpreadsPanel";
import SitemapPanel from "./SitemapPanel";
import Renderer from "../Renderer";
import EventEmitter from "events";
import Utils from "../utils/Utils";
import { sanitizeHtml } from "../utils/sanitizeHtml";

export default class Sidemenu extends EventEmitter {
    constructor(dataHelper) {
        super();

        this.sidemenuContainer = document.querySelector(".sidepanel");

        if (this.sidemenuContainer) {
            this.createPanels();
            this.bindMenuListeners();
            this._dataHelper = dataHelper;
            this._mainContainer = document.querySelector("body");
            window.injectMenuItem = this.injectMenuItem.bind(this);

            this._customItems = 0;
            window.dispatchEvent(Utils.createEvent("SidemenuLoaded"));
        }
    }

    createPanels() {
        let contactPanelElement = document.getElementById("maglrinterface-contact-form");
        if (contactPanelElement) {
            this.contactPanel = new ContactPanel(contactPanelElement);
        }
        let registerPanelElement = document.getElementById("maglrinterface-register-form");
        if (registerPanelElement) {
            this.registerPanel = new RegisterPanel(registerPanelElement);
        }
        let searchPanelElement = document.getElementById("maglrinterface-search-form");
        if (searchPanelElement) {
            this.searchPanel = new SearchPanel(searchPanelElement);
        }
        let sharePanelElement = document.getElementById("maglrinterface-share-panel");
        if (sharePanelElement) {
            this.sharePanel = new SharePanel(sharePanelElement);
            this.on("MENUTOGGLE", (event) => {
                if (event.action === "OPEN") {
                    let tab = this.sidemenuContainer.getAttribute("data-activetab");
                    if (tab === "share") {
                        this.setActiveTab(tab);
                    }
                }
            });
        }
        let spreadsPanelElement = document.getElementById("maglrinterface-spreads-panel");
        if (spreadsPanelElement) {
            this.spreadsPanel = new SpreadsPanel(spreadsPanelElement);
        }
        let sitemapPanelElement = document.getElementById("maglrinterface-sitemap-panel");
        if (sitemapPanelElement) {
            this.siteMapPanel = new SitemapPanel(sitemapPanelElement);
        }
    }

    injectMenuItem(name, contents) {
        try {
            this._customItems++;

            let tab = document.createElement("button");
            tab.setAttribute("data-tab", `customtab${this._customItems}`);
            ["sidepanel-link", "i-spread-link", "i-font-main"].forEach((classname) => {
                tab.classList.add(classname);
            });

            let text = document.createElement("div");
            text.classList.add("i-spread-link-text");
            text.innerText = name;
            tab.appendChild(text);
            document.querySelector(".sidepanel-menu-slider").appendChild(tab);

            tab.addEventListener("click", (e) => {
                this.setActiveTab(e.currentTarget.dataset.tab);
            });

            let pane = document.createElement("div");
            pane.setAttribute("data-tab-pane", `customtab${this._customItems}`);
            pane.classList.add(`tab-pane`);
            pane.innerHTML = sanitizeHtml(contents);
            document.querySelector(".sidepanel-content").appendChild(pane);
        } catch (error) {
            console.error("MAGLR ERROR: InjectMenuItem failed for:", name, contents, error);
        }
    }

    bindMenuListeners() {
        document.querySelectorAll(".open-menu").forEach((entry) => {
            entry.addEventListener("click", (e) => this.openMenu(e.detail < 1));
        });
        const shade = document.querySelector(".sidemenu-shade");
        shade && shade.addEventListener("click", () => this.closeMenu());

        const closeMobile = document.querySelector(".close-sidepanel-mobile");
        closeMobile && closeMobile.addEventListener("click", () => this.closeMenu());

        const close = document.querySelector(".close-sidepanel");
        close && close.addEventListener("click", () => this.closeMenu());

        document.querySelectorAll(".sidepanel-spread-title").forEach((entry) => {
            entry.addEventListener("click", (e) => e.currentTarget.classList.toggle("show"));
        });

        //bind optional icons and extra navigation menu icons, trigger menu and the right tab
        document
            .querySelectorAll(".menu-optional-icons [data-tab], .menubar-extra-icons [data-tab]")
            .forEach((entry) => {
                entry.addEventListener("click", (e) => {
                    this.setActiveTab(e.currentTarget.dataset.tab);
                    this.openMenu(e.detail < 1);
                });
            });

        //bind side panel tab menu
        this._tabs = Array.prototype.slice.call(document.querySelectorAll(".sidepanel-menu [data-tab]"));
        this._tabs.forEach((entry) => {
            entry.addEventListener("click", (e) => {
                this.setActiveTab(e.currentTarget.dataset.tab);
            });

            if (this.sidemenuContainer.dataset.activetab === entry.dataset.tab) {
                entry.setAttribute("aria-selected", "true");
            }
        });

        Renderer.inverseColors(document.getElementById("sidemenu"));
    }

    loadInitScriptForPanel(panel) {
        if (!panel) {
            return;
        }
        panel.init();
    }

    handleTabActivation(tab) {
        switch (tab) {
            case "share":
                if (this.sharePanel) {
                    let issue = this._dataHelper.getCurrentIssue();
                    issue && this.sharePanel.activated(issue, this._dataHelper.getCurrentSpreads());
                } else {
                    return;
                }
                break;
            case "contact":
                this.loadInitScriptForPanel(this.contactPanel);
                break;
            case "search":
                if (!this.searchPanel) {
                    return;
                }
                break;
            case "register":
                this.loadInitScriptForPanel(this.registerPanel);
                break;
        }
    }

    setActiveTab(tab) {
        this.handleTabActivation(tab);

        this.sidemenuContainer.setAttribute("data-activetab", tab);
        this.sidemenuContainer
            .querySelector(`[data-tab=${tab}]`)
            .scrollIntoView({ block: "center", inline: "center", behavior: "smooth" }); //center scrollview if needed

        this.focusTrap && this.activateFocusTrap(tab);

        this._tabs &&
            this._tabs.forEach((el) => {
                if (el.dataset.tab === tab) {
                    el.setAttribute("aria-selected", "true");
                    el.classList.add("active");
                } else {
                    el.setAttribute("aria-selected", "false");
                    el.classList.remove("active");
                }
            });

        return this;
    }

    setFirstTabActive() {
        let firstTab = this.sidemenuContainer?.querySelector("[data-tab]")?.dataset.tab;
        firstTab && this.setActiveTab(firstTab);
    }

    findActiveTab() {
        return (
            this.sidemenuContainer?.getAttribute("data-activetab") ||
            this.sidemenuContainer?.querySelector("[data-tab]")?.dataset.tab
        );
    }

    openMenu(enableFocusTrap = false) {
        this.emit("MENUTOGGLE", { action: "OPEN" });
        const sidemenu = document.getElementById("sidemenu");
        sidemenu && sidemenu.setAttribute("aria-hidden", "false");

        this.handleTabActivation(this.findActiveTab());

        sidemenu && sidemenu.setAttribute("aria-expanded", "true");
        this._mainContainer.setAttribute("data-sidemenu", "open");

        if (enableFocusTrap) {
            this.activateFocusTrap(this.sidemenuContainer.dataset.activetab);
        }
        return this;
    }

    activateFocusTrap(activeTab) {
        this.deactivateFocusTrap();
        this.sidemenuContainer.classList.add("focus-trapped");

        this.focusTrap = Utils.trapFocus([
            this.sidemenuContainer?.querySelector(`[data-tab-pane="${activeTab}"]`),
            this.sidemenuContainer?.querySelector(".sidepanel-menu-slider"),
            document.querySelector(".close-sidepanel"),
        ]);

        let activeIndex;
        this.handleKeyboard = (event) => {
            const key = event.which || event.keyCode;
            switch (key) {
                case 27: //escape
                    this.closeMenu(0);
                    event.stopPropagation();
                    this.deactivateFocusTrap();
                    break;
                case 37: //arrow left
                case 39: //arrow right
                    event.stopPropagation();
                    activeIndex = this._tabs.findIndex(
                        (tab) => tab.dataset.tab === this.sidemenuContainer.dataset.activetab,
                    );
                    if (activeIndex > -1) {
                        if (key === 37) {
                            if (activeIndex === 0) {
                                // first
                                this.setActiveTab(this._tabs[this._tabs.length - 1].dataset.tab);
                            } else {
                                this.setActiveTab(this._tabs[activeIndex - 1].dataset.tab);
                            }
                        }
                        if (key === 39) {
                            if (activeIndex === this._tabs.length - 1) {
                                // last
                                this.setActiveTab(this._tabs[0].dataset.tab);
                            } else {
                                this.setActiveTab(this._tabs[activeIndex + 1].dataset.tab);
                            }
                        }
                    }
                    break;
            }
        };
        document.addEventListener("keyup", this.handleKeyboard);
    }

    deactivateFocusTrap() {
        this.sidemenuContainer.classList.remove("focus-trapped");
        this.handleKeyboard && document.removeEventListener("keyup", this.handleKeyboard);
        this.focusTrap && Utils.releaseFocus(this.focusTrap);
        delete this.focusTrap;
    }

    closeMenu(delay = 0) {
        if (this._mainContainer.getAttribute("data-sidemenu") !== "closed") {
            const sidemenu = document.getElementById("sidemenu");
            sidemenu && sidemenu.setAttribute("aria-hidden", "true");
            sidemenu && sidemenu.setAttribute("aria-expanded", "false");
            this.deactivateFocusTrap();

            setTimeout(() => {
                this._mainContainer.setAttribute("data-sidemenu", "closed");
                this.emit("MENUTOGGLE", { action: "CLOSE" });
            }, delay);
        }
    }

    renderSpreadsPanel(spreads, issue) {
        return this.spreadsPanel && this.spreadsPanel.renderSpreads(spreads, issue);
    }

    renderSitemap(data) {
        this.siteMapPanel && this.siteMapPanel.renderSitemap(data);
    }
}
